'use client';

import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConnectorAccess } from '@/hooks/use-connector-access';
import { buildWorkspaceAwarePath } from '@/lib/active-workspace';
import { getLocaleRelativePath } from '@/lib/bare-routes';
import { CONNECTOR_HOME_SCREEN, SPECS_ACTIONS_SCREEN } from '@/lib/connector-access';
import { domId } from '@/lib/dom-id';
import { pushWithMinimumDelay } from '@/lib/navigation';

export type CreatedConnector = {
  systemId: string;
  connectorName: string;
};

type ConnectorCreatedModalProps = {
  connector: CreatedConnector | null;
  onClose: () => void;
};

const BRAND_GRADIENT = 'linear-gradient(90deg, #713D98 0%, #3E3D98 140.38%)';
const BRAND_GRADIENT_TEXT_CLASS =
  'bg-gradient-to-r from-[#713d98] to-[#3e3d98] to-[140.38%] bg-clip-text text-transparent';

export default function ConnectorCreatedModal({ connector, onClose }: ConnectorCreatedModalProps) {
  const t = useTranslations('ConnectorOnboardingCompleted');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { canAccessScreen } = useConnectorAccess();
  const canGoDashboard = canAccessScreen(CONNECTOR_HOME_SCREEN);
  const canViewDetail = canAccessScreen(SPECS_ACTIONS_SCREEN);

  const systemId = connector?.systemId ?? '';
  const connectorName = connector?.connectorName || t('fallbackConnectorName');

  const handleGoDashboard = () => {
    onClose();
    if (getLocaleRelativePath(pathname, locale) === '/') return;
    void pushWithMinimumDelay(router, buildWorkspaceAwarePath(`/${locale}`), {
      skipUnsavedCheck: true,
    });
  };

  const handleViewDetail = () => {
    if (!systemId) {
      handleGoDashboard();
      return;
    }
    onClose();
    const params = new URLSearchParams({ system_id: systemId, name: connectorName });
    void pushWithMinimumDelay(
      router,
      buildWorkspaceAwarePath(`/${locale}/specs/actions`, params),
      { skipUnsavedCheck: true }
    );
  };

  return (
    <Dialog
      open={connector !== null}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        showClose={false}
        data-testid={domId('mdl', 'connector-created')}
        className="w-[calc(100vw-32px)] max-w-[435px] rounded-2xl p-6"
      >
        <div className="flex flex-col items-center gap-8">
          <div className="flex w-full flex-col items-center gap-2">
            <div className="flex w-full flex-col items-center gap-4">
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#f0fdf4]">
                <Image
                  src="/onboarding/completion-trophy.svg"
                  alt=""
                  width={36}
                  height={36}
                  aria-hidden="true"
                />
                <Image
                  src="/onboarding/completion-spark.svg"
                  alt=""
                  width={22}
                  height={21}
                  aria-hidden="true"
                  className="absolute -right-1 -top-1"
                />
              </div>
              <div className="flex w-full flex-col items-center gap-1 text-center">
                <DialogTitle
                  data-testid={domId('txt', 'connector-created-title')}
                  className="text-xl font-semibold leading-[1.4] tracking-normal text-[#0b0809]"
                >
                  {t('title')}
                </DialogTitle>
                <DialogDescription
                  data-testid={domId('txt', 'connector-created-subtitle')}
                  className="text-sm font-normal leading-[1.4] text-[#444746] [overflow-wrap:anywhere]"
                >
                  {t('subtitle.prefix')}{' '}
                  <span
                    data-testid={domId('txt', 'connector-created-subtitle-name')}
                    className={`font-semibold ${BRAND_GRADIENT_TEXT_CLASS}`}
                  >
                    {connectorName}
                  </span>{' '}
                  {t('subtitle.suffix')}
                </DialogDescription>
              </div>
            </div>
            <ul
              data-testid={domId('txt', 'connector-created-highlights')}
              className="w-full list-disc space-y-2 pl-[21px] text-left text-sm font-normal leading-[1.4] text-[#444746]"
            >
              <li>{t('highlights.connected')}</li>
              <li>{t('highlights.validated')}</li>
              <li>{t('highlights.copilot')}</li>
            </ul>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {canGoDashboard && (
              <Button
                data-testid={domId('btn', 'connector-created-go-dashboard')}
                type="button"
                variant="unstyled"
                className="h-10 rounded-lg border border-[#713d98] bg-white px-5 text-sm font-medium leading-[1.4]"
                onClick={handleGoDashboard}
              >
                <span className={BRAND_GRADIENT_TEXT_CLASS}>{t('actions.goToDashboard')}</span>
              </Button>
            )}
            {canViewDetail && (
              <Button
                data-testid={domId('btn', 'connector-created-view-detail')}
                type="button"
                variant="unstyled"
                className="h-10 rounded-lg px-5 text-sm font-medium leading-[1.4] text-white"
                style={{ background: BRAND_GRADIENT }}
                onClick={handleViewDetail}
              >
                {t('actions.viewDetail')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
